import { useCallback, useEffect, useRef, useState } from "react";
import {
  startOnboardingRun,
  track,
  type OnboardingEntry,
  type OnboardingEventProps
} from "../../../lib/onboardingAnalytics";
import { trackOnboardingFinish, trackOnboardingStart } from "../../../lib/conversionTracking";
import type { OnboardingStepId } from "@shared/modules/telemetry/onboardingContract";
import type { WizardTrainingChoice } from "./steps/MethodStep";
import type { WizardStepId } from "./wizardMeta";

// The wizard's step ids ARE the contract's — fails to compile if either drifts.
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const STEP_IDS_MATCH: Same<WizardStepId, OnboardingStepId> = true;
void STEP_IDS_MATCH;

/**
 * Funnel analytics for one wizard run (open → steps → finish | abandon).
 * Mount = `opened` (+ the ad layer's `onboarding_start`); every step change =
 * `step_viewed`; the orchestrator calls `markFinished()` on a successful
 * finish, BEFORE handing off to the host (the host may unmount the wizard);
 * unmount or page hide without a finish = `abandoned` at the last step.
 *
 * StrictMode-safe: the run handle lives in state, sends are ref-guarded, and
 * the unmount abandon is deferred one tick so a dev double-mount cancels it.
 */
export function useOnboardingAnalytics({
  entry,
  step,
  stepOrder,
  hasRace,
  trainingChoice,
  skippedMethod,
  csrfToken
}: {
  entry: OnboardingEntry;
  step: WizardStepId;
  stepOrder: readonly WizardStepId[];
  hasRace: boolean | null;
  trainingChoice: WizardTrainingChoice;
  skippedMethod: boolean;
  csrfToken: string | null;
}): { markFinished: () => void } {
  const csrfRef = useRef(csrfToken);
  csrfRef.current = csrfToken;
  const [run] = useState(() => startOnboardingRun(entry, () => csrfRef.current));

  // Snapshot of the run's current position. The training answer counts only
  // once the method question is behind the athlete (or skipped) — before that
  // it is just the wizard's pre-selected default.
  const stepIndex = Math.max(0, stepOrder.indexOf(step));
  const methodIndex = stepOrder.indexOf("method");
  const props: OnboardingEventProps = {
    step,
    stepIndex,
    stepCount: stepOrder.length,
    path: hasRace === null ? null : hasRace ? "race" : "no_race",
    trainingChoice: methodIndex === -1 || methodIndex < stepIndex ? trainingChoice : null,
    skippedMethod
  };
  const latest = useRef(props);
  latest.current = props;

  const sent = useRef({
    opened: false,
    finished: false,
    abandoned: false,
    lastStep: null as WizardStepId | null
  });

  useEffect(() => {
    if (sent.current.opened) return;
    sent.current.opened = true;
    track(run, "opened", latest.current);
    trackOnboardingStart();
  }, [run]);

  useEffect(() => {
    if (sent.current.lastStep === step) return;
    sent.current.lastStep = step;
    track(run, "step_viewed", latest.current);
  }, [run, step]);

  const abandon = useCallback(() => {
    if (sent.current.finished || sent.current.abandoned) return;
    sent.current.abandoned = true;
    track(run, "abandoned", latest.current);
  }, [run]);

  const pendingAbandon = useRef<number | undefined>(undefined);
  useEffect(() => {
    // A StrictMode remount re-runs this before the deferred abandon fires.
    window.clearTimeout(pendingAbandon.current);
    window.addEventListener("pagehide", abandon);
    return () => {
      window.removeEventListener("pagehide", abandon);
      pendingAbandon.current = window.setTimeout(abandon, 0);
    };
  }, [abandon]);

  const markFinished = useCallback(() => {
    if (sent.current.finished) return;
    sent.current.finished = true;
    track(run, "finished", latest.current);
    trackOnboardingFinish(latest.current.path ?? null);
  }, [run]);

  return { markFinished };
}
